import type { RoolChannel } from '@rool-dev/sdk';
import { get } from 'svelte/store';
import { channel } from '../stores';
import { rebuildGraph } from './graph';
import {
	getCachedObject,
	markNodeLocallyModified,
	patchCachedObject,
	uncacheObject,
} from './canvas-object-store';

type Position = { x: number; y: number };
type Size = { width: number; height: number };

function getCurrentChannel(): RoolChannel | null {
	return get(channel);
}

export function moveNodes(
	nodes: Array<{ id: string; position: Position }>,
): void {
	const currentChannel = getCurrentChannel();
	if (!currentChannel || nodes.length === 0) return;

	for (const { id, position } of nodes) {
		markNodeLocallyModified(id);
		patchCachedObject(id, { _x: position.x, _y: position.y });
	}
	rebuildGraph();

	currentChannel.checkpoint('move-nodes');
	Promise.all(
		nodes.map(({ id, position }) =>
			currentChannel.updateObject(id, {
				data: { _x: position.x, _y: position.y },
			}),
		),
	).catch((error) => {
		console.error('Error updating node positions:', error);
	});
}

export function resizeNode(
	nodeId: string,
	position: Position,
	size: Size,
	autofitData: Record<string, unknown>,
): void {
	const currentChannel = getCurrentChannel();
	if (!currentChannel) return;

	markNodeLocallyModified(nodeId);
	patchCachedObject(nodeId, {
		_x: position.x,
		_y: position.y,
		_width: size.width,
		_height: size.height,
		...autofitData,
	});
	rebuildGraph();

	currentChannel.checkpoint('resize-node');
	currentChannel
		.updateObject(nodeId, {
			data: {
				_x: position.x,
				_y: position.y,
				_width: size.width,
				_height: size.height,
				...autofitData,
			},
		})
		.catch((err: unknown) => {
			console.error('[canvas] Failed to persist resize:', err);
		});
}

export function rotateNode(nodeId: string, rotation: number): void {
	const currentChannel = getCurrentChannel();
	if (!currentChannel) return;

	markNodeLocallyModified(nodeId);
	patchCachedObject(nodeId, { _rotation: rotation });
	rebuildGraph();

	currentChannel.checkpoint('rotate-node');
	currentChannel
		.updateObject(nodeId, { data: { _rotation: rotation } })
		.catch((err: unknown) => {
			console.error('[canvas] Failed to persist rotation:', err);
		});
}

export function layoutNodes(
	nodes: Array<{ id: string; position: Position }>,
): void {
	const currentChannel = getCurrentChannel();
	if (!currentChannel || nodes.length === 0) return;

	for (const { id, position } of nodes) {
		markNodeLocallyModified(id);
		patchCachedObject(id, { _x: position.x, _y: position.y });
	}
	rebuildGraph();

	currentChannel.checkpoint('auto-layout');
	Promise.all(
		nodes.map(({ id, position }) =>
			currentChannel.updateObject(id, {
				data: { _x: position.x, _y: position.y },
			}),
		),
	).catch((error) => {
		console.error('[canvas] Error persisting layout positions:', error);
	});
}

export async function persistNodeData(
	nodeId: string,
	data: Record<string, unknown>,
	checkpointMessage: string,
): Promise<void> {
	const currentChannel = getCurrentChannel();
	if (!currentChannel) return;

	markNodeLocallyModified(nodeId);
	patchCachedObject(nodeId, data);
	rebuildGraph();

	currentChannel.checkpoint(checkpointMessage);
	await currentChannel.updateObject(nodeId, { data });
}

export async function createEdge(
	sourceNodeId: string,
	targetNodeId: string,
): Promise<void> {
	const currentChannel = getCurrentChannel();
	if (!currentChannel) return;

	try {
		currentChannel.checkpoint('create-edge');
		const sourceObj =
			getCachedObject(sourceNodeId) ??
			(await currentChannel.getObject(sourceNodeId));
		const existing = (sourceObj?.connects_to as string[]) ?? [];
		patchCachedObject(sourceNodeId, {
			connects_to: [...existing, targetNodeId],
		});
		rebuildGraph();
		await currentChannel.updateObject(sourceNodeId, {
			data: { connects_to: [...existing, targetNodeId] },
		});
	} catch (err) {
		console.error('[canvas] Failed to create edge:', err);
	}
}

export function deleteNodes(nodeIds: string[]): void {
	const currentChannel = getCurrentChannel();
	if (!currentChannel || nodeIds.length === 0) return;

	for (const nodeId of nodeIds) {
		uncacheObject(nodeId);
	}
	rebuildGraph();

	currentChannel.checkpoint('delete-node');
	currentChannel.deleteObjects(nodeIds).catch((err: unknown) => {
		console.error('[canvas] Failed to delete nodes:', err);
	});
}

export function deleteNode(nodeId: string): void {
	deleteNodes([nodeId]);
}

export function deleteEdges(edgeIds: string[]): void {
	const currentChannel = getCurrentChannel();
	if (!currentChannel || edgeIds.length === 0) return;

	currentChannel.checkpoint('delete-edge');
	let didPatchCache = false;
	for (const edgeId of edgeIds) {
		const firstColon = edgeId.indexOf(':');
		const lastColon = edgeId.lastIndexOf(':');
		if (firstColon === -1 || lastColon === -1 || firstColon === lastColon) {
			console.error('[canvas] Invalid edge ID format:', edgeId);
			continue;
		}
		const sourceId = edgeId.substring(0, firstColon);
		const fieldName = edgeId.substring(firstColon + 1, lastColon);
		const targetId = edgeId.substring(lastColon + 1);
		const sourceObj = getCachedObject(sourceId);
		const existing = sourceObj?.[fieldName];
		let data: Record<string, unknown> | null = null;
		if (Array.isArray(existing)) {
			const filtered = existing.filter((v: unknown) => v !== targetId);
			data = { [fieldName]: filtered.length > 0 ? filtered : null };
		} else if (existing === targetId) {
			data = { [fieldName]: null };
		}
		if (!data) continue;
		patchCachedObject(sourceId, data);
		didPatchCache = true;
		currentChannel.updateObject(sourceId, { data }).catch((err: unknown) => {
			console.error('[canvas] Failed to delete edge:', err);
		});
	}
	if (didPatchCache) {
		rebuildGraph();
	}
}
